<script lang="ts" setup>
import { ref, watch } from 'vue'
import type { PayPageConfig } from '@/composables/payment'
import {
  createShowPaymentMethodSelector,
  useFixedBottomPayment,
  usePurchase,
} from '@/composables/payment'
import PaymentMethodSelector from '@/composables/payment/components/PaymentMethodSelector.novelmello.vue'
import { useAxonViewItem } from '@/composables/axon/useAxonViewItem'
import { useFbqAddToCart } from '@/composables/fbq/useFbqAddToCart'
import { useGotoBack } from '@/composables'
import { useUserStore } from '@/stores'
import { t } from '@/i18n/utils'

defineProps<{
  config: PayPageConfig
}>()

const gotoBack = useGotoBack()
const user = useUserStore()

// ===== 以下逻辑与 PayPage.default.vue 完全一致，不做任何删改 =====
// 使用充值 composable（支持多支付方式）
const {
  templateProducts,
  PayTemplate,
  currentPaymentMethod,
  availablePaymentMethods,
  selectPaymentMethod,
} = usePurchase('store')

// 是否显示支付方式选择器
const showPaymentMethodSelector = createShowPaymentMethodSelector(availablePaymentMethods)

// 判断是否需要固定在底部
const { isFixedBottom, containerRef, checkContentHeight } = useFixedBottomPayment()

// 监听支付方式切换，重新计算高度
watch(currentPaymentMethod, () => {
  checkContentHeight()
})

// Axon view_item 上报（商品加载 + 支付方式切换均触发，Set 内部去重）
const { reportViewItem } = useAxonViewItem()
watch(templateProducts, (val) => {
  if (val.length)
    reportViewItem(val, currentPaymentMethod.value, 'Coins Package')
})

const { reportAddToCart } = useFbqAddToCart()
watch(templateProducts, (val) => {
  for (const p of val)
    reportAddToCart(p.productId, 'Coins Package', p.price ?? 0)
})
// ===== 逻辑区结束 =====

// —— 纯 UI：商品卡选中态（不参与任何请求参数，支付始终作用于被点击的 item）——
const selectedProductId = ref<any>(null)
watch(templateProducts, (val) => {
  if (!val.length) {
    selectedProductId.value = null
    return
  }
  // 列表随渠道刷新后，选中项若已不存在则回落首档
  const exists = val.some((item: any) => item.productId === selectedProductId.value)
  if (!exists)
    selectedProductId.value = val[0].productId
}, { immediate: true })

function handleProductClick(item: any) {
  selectedProductId.value = item.productId
  // 支付发起链路原样调用
  PayTemplate(item)
}

function isSelected(item: any) {
  return selectedProductId.value === item.productId
}

// 价格两位小数（纯展示格式化）
function formatPrice(price?: number | string) {
  return Number(price || 0).toFixed(2)
}

// 赠币比例，取值口径与 default 组件一致
function bonusRate(item: any) {
  if (!item?.coinsBuy)
    return 0
  return Number.parseFloat(((Number(item.coinsBonus || 0) / Number(item.coinsBuy)) * 100).toFixed(2))
}
</script>

<template>
  <div class="nm-store">
    <!-- 顶栏：返回 + 居中标题（设计稿的 restore 入口按最终裁决不渲染：H5 无恢复购买实现） -->
    <header class="nm-header">
      <button type="button" class="nm-header-btn" aria-label="Back" @click="gotoBack()">
        <i-lucide-chevron-left class="nm-header-icon" />
      </button>
      <h1 class="nm-header-title">
        {{ t('Store') }}
      </h1>
      <!-- 占位块，保证标题视觉居中 -->
      <span class="nm-header-btn" aria-hidden="true" />
    </header>

    <div ref="containerRef" class="nm-body">
      <!-- 余额 -->
      <div class="nm-balance">
        <span class="nm-balance-num">{{ user.$state.coinBalance }}</span>
        <span class="nm-balance-unit">{{ config.texts.coins }}</span>
      </div>

      <!-- 金币档位 -->
      <div class="nm-products">
        <button
          v-for="item in templateProducts"
          :key="item.productId"
          type="button"
          class="nm-product"
          :class="{ 'is-active': isSelected(item) }"
          @click="handleProductClick(item)"
        >
          <!-- 运营角标：判定与 default 一致 -->
          <span v-if="item.cornerTip === 1 && item.marketingText" class="nm-product-tag">
            {{ item.marketingText }}
          </span>

          <div class="nm-product-left">
            <i-lucide-coins class="nm-coin-icon" />
            <div class="nm-product-info">
              <p class="nm-product-main">
                <span class="nm-product-amount">{{ item.coinsBuy }}</span>
                <span class="nm-product-unit">{{ config.texts.coins }}</span>
              </p>
              <p v-if="Number(item.coinsBonus) > 0" class="nm-product-bonus">
                +{{ item.coinsBonus }} <span class="nm-lower">{{ config.texts.coins }}</span>
              </p>
            </div>
          </div>

          <div class="nm-product-right">
            <span class="nm-product-price">${{ formatPrice(item.price) }}</span>
            <span v-if="item.cornerTip" class="nm-product-rate">
              +{{ bonusRate(item) }}% <span class="nm-lower">{{ config.texts.coins }}</span>
            </span>
          </div>
        </button>
      </div>

      <!-- 支付方式选择器 - 动态位置（逻辑同 default；PaymentMethodSelector 用 novelmello 皮肤，props / emit 与通用组件一致） -->
      <div
        v-if="showPaymentMethodSelector"
        class="nm-methods"
        :class="{ 'is-fixed': isFixedBottom }"
      >
        <PaymentMethodSelector
          variant="large"
          :model-value="currentPaymentMethod"
          :available-methods="availablePaymentMethods"
          @update:model-value="selectPaymentMethod"
        />
      </div>

      <!-- Tips -->
      <div class="nm-tips" :class="{ 'is-safe': isFixedBottom && showPaymentMethodSelector }">
        <p class="nm-tips-title">
          {{ config.texts.tips }}
        </p>
        <p class="nm-tips-line">
          {{ config.texts.tip1 }}
        </p>
        <p class="nm-tips-line">
          {{ config.texts.tip2 }}
        </p>
        <p class="nm-tips-line">
          {{ config.texts.tip3 }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* 色值随组件落地（与 SubscribePage.novelmello.vue 同口径），字体族取 htmls/novelmello.html 定义的变量 */
.nm-store {
  --nm-ink-1: #2B1B34;
  --nm-ink-3: #AB9BB8;
  --nm-purple: #7C4DE8;
  --nm-pink: #F0509B;
  --nm-gold: #FFB23E;
  --nm-card-border: #F2EAF4;
  --nm-card-active-bg: #FDEFF6;
  --nm-page-bg: #FBF6FB;
  --nm-tag-gradient: linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%);

  min-height: 100vh;
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  background: var(--nm-page-bg);
  font-family: var(--nm-font-sans, 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif);
  box-sizing: border-box;
}

/* ===== 顶栏 ===== */
.nm-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--nm-page-bg);
}

.nm-header-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--nm-ink-1);
}

.nm-header-icon {
  width: 24px;
  height: 24px;
}

.nm-header-title {
  flex: 1;
  min-width: 0;
  margin: 0 12px;
  text-align: center;
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif);
  font-size: 19px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--nm-ink-1);
}

/* ===== 余额 ===== */
.nm-balance {
  display: flex;
  align-items: baseline;
  padding: 4px 16px 16px;
}

.nm-balance-num {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif);
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--nm-purple);
}

.nm-balance-unit {
  margin-left: 6px;
  font-size: 13px;
  line-height: 1.2;
  color: var(--nm-ink-3);
  text-transform: lowercase;
}

/* ===== 金币档位卡 ===== */
.nm-products {
  padding: 0 16px;
}

.nm-product {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--nm-card-border);
  border-radius: 16px;
  background: #FFFFFF;
  text-align: left;
  box-sizing: border-box;
  transition: background 0.2s ease, border-color 0.2s ease;
}

/* 卡间距不用 flex gap（项目 scan:flex-gap 兼容性约束） */
.nm-product + .nm-product {
  margin-top: 12px;
}

.nm-product.is-active {
  border: 1.5px solid var(--nm-pink);
  background: var(--nm-card-active-bg);
}

.nm-product-tag {
  position: absolute;
  top: 0;
  left: 0;
  height: 18px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  border-radius: 16px 0 12px 0;
  background: var(--nm-tag-gradient);
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.nm-product-left {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
}

.nm-coin-icon {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  margin-right: 12px;
  color: var(--nm-gold);
}

.nm-product-info {
  min-width: 0;
}

.nm-product-main {
  margin: 0;
  display: flex;
  align-items: baseline;
}

.nm-product-amount {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif);
  font-size: 19px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--nm-ink-1);
}

.nm-product-unit {
  margin-left: 5px;
  font-size: 12px;
  line-height: 1.2;
  color: var(--nm-ink-3);
  text-transform: lowercase;
}

.nm-product-bonus {
  margin: 3px 0 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--nm-purple);
}

.nm-product-right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-left: 12px;
  text-align: right;
}

.nm-product-price {
  font-family: var(--nm-font-display, 'Baloo 2', 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
  color: var(--nm-ink-1);
  white-space: nowrap;
}

.nm-product-rate {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.2;
  color: var(--nm-ink-3);
  white-space: nowrap;
}

.nm-lower {
  text-transform: lowercase;
}

/* ===== 支付方式 ===== */
.nm-methods {
  padding: 0 16px;
}

.nm-methods.is-fixed {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  padding: 0 16px calc(16px + env(safe-area-inset-bottom));
  background: rgba(251, 246, 251, 0.96);
  backdrop-filter: blur(6px);
}

/* stylelint-disable-next-line unit-case */
@media (min-width: 768px) {
  .nm-methods.is-fixed {
    left: 50%;
    right: auto;
    width: 750PX;
    max-width: 750PX;
    transform: translateX(-50%);
  }
}

/* ===== Tips ===== */
.nm-tips {
  padding: 20px 16px 0;
}

/* 渠道选择器固定到底部时，给 Tips 留出安全区 */
.nm-tips.is-safe {
  padding-bottom: 96px;
}

.nm-tips-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--nm-ink-3);
}

.nm-tips-line {
  margin: 6px 0 0;
  font-size: 11px;
  line-height: 1.5;
  color: var(--nm-ink-3);
}
</style>
